import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { User } from '@supabase/supabase-js';
import { AuthService } from '@/services';
import { clearStores } from './storesSlice';
import { persistor } from '@/state/store';
import { logger } from '@/lib/logger';

export interface PublicUser {
  uid: string;
  name: string;
  name_locale: string | null;
  phone: string;
  email: string;
  address: string | null;
  address_locale: string | null;
  is_superadmin: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthState {
  user: User | null;
  publicUser: PublicUser | null;
  isLoading: boolean;
  error: string | null;
  tab: 'password' | 'otp';
}

const initialState: AuthState = {
  user: null,
  publicUser: null,
  isLoading: false,
  error: null,
  tab: 'otp',
};

const signUp = createAsyncThunk(
  'auth/signUp',
  async ({ email, password, userNumber }: { email: string; password: string; userNumber: string }) => {
    return AuthService.signUp(email, password, userNumber);
  }
);

const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ email, password }: { email: string; password: string }, { dispatch }) => {
    const user = await AuthService.signIn(email, password);
    let publicUser = null;
    if (user && user.id) {
      publicUser = await AuthService.getPublicUserByAuthId(user.id);
    }
    // Clear stores when signing in to ensure no data leakage between users
    dispatch(clearStores());
    return { user, publicUser };
  }
);

const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { dispatch }) => {
    await AuthService.signOut();
    // Clear stores when signing out
    dispatch(clearStores());
    // Clear saved route so next login lands on dashboard, not a stale page
    localStorage.removeItem('lastRoute');
    // Clear the entire Redux store (reset all slices)
    dispatch({ type: 'RESET_APP_STATE' });
    // Purge persisted redux state
    persistor.purge();
  }
);



// Simple auth user retrieval - only returns auth user from session
export const getAuthUser = createAsyncThunk(
  'auth/getAuthUser',
  async () => {
    try {
      const authUser = await AuthService.getAuthUser();
      return authUser; // Just return the auth user directly
    } catch (error) {
      logger.warn('Auth check failed', { error, component: 'authSlice' });
      return null;
    }
  }
);

// Separate thunk for fetching public user (used conditionally)
export const getPublicUser = createAsyncThunk(
  'auth/getPublicUser',
  async (authUserId: string) => {
    try {
      const publicUser = await AuthService.getPublicUserByAuthId(authUserId);
      return publicUser;
    } catch (error) {
      logger.warn('Failed to fetch public user', { error, component: 'authSlice' });
      return null;
    }
  }
);

// Update public user profile
export const updatePublicUser = createAsyncThunk(
  'auth/updatePublicUser',
  async ({ authUserId, updates }: { authUserId: string; updates: { name?: string; name_locale?: string; address?: string; address_locale?: string } }) => {
    try {
      const updatedUser = await AuthService.updateUserProfile(authUserId, updates);
      return updatedUser;
    } catch (error) {
      logger.warn('Failed to update public user', { error, component: 'authSlice' });
      throw error;
    }
  }
);

// Export aliases to match the import names used in components
export const login = signIn;
export const register = signUp;
export const logout = signOut;

// OTP: Send OTP to phone
export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async (phone: string, { rejectWithValue }) => {
    try {
      const { error } = await AuthService.signInWithPhoneOtp(phone);
      if (error) {
        return rejectWithValue(error.message || 'Failed to send OTP');
      }
      return true;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to send OTP');
    }
  }
);

// OTP: Verify OTP
export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ phone, otp }: { phone: string; otp: string }, { rejectWithValue }) => {
    try {
      const { user, error } = await AuthService.verifyPhoneOtp(phone, otp);
      if (error) {
        return rejectWithValue(error.message || 'Invalid OTP');
      }
      let publicUser = null;
      if (user && user.id) {
        publicUser = await AuthService.getPublicUserByAuthId(user.id);
      }
      return { user, publicUser };
    } catch (err: any) {
      return rejectWithValue(err.message || 'OTP verification failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTab: (state, action) => {
      state.tab = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Sign Up
    builder
      .addCase(signUp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to sign up';
      });

    // Sign In
    builder
      .addCase(signIn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.publicUser = action.payload.publicUser;
        state.error = null;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to sign in';
      });

    // Sign Out
    builder
      .addCase(signOut.fulfilled, (state) => {
        state.user = null;
        state.error = null;
      });



    // Get Auth User (fast session-based auth check)
    builder
      .addCase(getAuthUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAuthUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload; // Just set the auth user
        state.error = null;
      })
      .addCase(getAuthUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to get auth user';
      });

    // Get Public User (conditional fetch)
    builder
      .addCase(getPublicUser.fulfilled, (state, action) => {
        state.publicUser = action.payload;
      });

    // Update Public User
    builder
      .addCase(updatePublicUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePublicUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.publicUser = action.payload;
        state.error = null;
      })
      .addCase(updatePublicUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update profile';
      });

    // OTP: Send OTP (use isLoading/error for consistency)
    builder
      .addCase(sendOtp.pending, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to send OTP';
      });

    // OTP: Verify OTP (set user and publicUser on success)
    builder
      .addCase(verifyOtp.pending, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.user = action.payload.user;
        state.publicUser = action.payload.publicUser;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'OTP verification failed';
      });
  },
});

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectPublicUser = (state: { auth: AuthState }) => state.auth.publicUser;

export const { setTab } = authSlice.actions; 